/**
 * Wealthica2 flow toast bridge hook
 *
 * Responsibilities:
 * - Rehydrate in-flight connection flow toasts after navigation
 * - Listen for persistence/positions events and update toast/progress
 * - Dismiss flow toast when persistence deletion occurs
 */
import { useEffect } from 'react';
import { rehydrateFlowToast, updateFlowProgress, updateFlowToast, dismissFlowToast, isFlowToastActive } from '../utils/flowToast';

export function useWealthicaFlowToastBridge(wealthicaAccountsLength: number) {
  useEffect(() => {
    const state = rehydrateFlowToast();
    if (state && state.id === 'wealthica-flow' && (wealthicaAccountsLength || 0) === 0) {
      try { updateFlowProgress(state.id, 55, 'Connection successful!', 'Fetching latest accounts and positions'); } catch { /* noop */ }
    }
    let earlySuccessTimer: ReturnType<typeof setTimeout> | null = null;

    const onPersistComplete = (evt: any) => {
      const detail = evt?.detail || {};
      const accountsSavedCount = Number(detail?.accountsSavedCount || 0);
      const positionsSavedCount = Number(detail?.positionsSavedCount || 0);
      if (!isFlowToastActive()) return;
      if (positionsSavedCount > 0) {
        try { updateFlowProgress('wealthica-flow', 100, 'Saved', 'All set'); } catch { /* noop */ }
        try { updateFlowToast('wealthica-flow', 'success', 'Data saved'); } catch { /* noop */ }
      } else {
        try { updateFlowProgress('wealthica-flow', 90, 'Connected. Saving to your workspace…'); } catch { /* noop */ }
      }
    };

    const onPositionsLoaded = (evt: any) => {
      const detail = evt?.detail || {};
      const count = Number(detail?.count || 0);
      if (!isFlowToastActive()) return;
      if (count === 0) {
        try { updateFlowProgress('wealthica-flow', 100, 'Saved', 'All set'); } catch { /* noop */ }
        try { updateFlowToast('wealthica-flow', 'success', 'Accounts saved'); } catch { /* noop */ }
      } else if (count > 0) {
        try { if (earlySuccessTimer) clearTimeout(earlySuccessTimer); } catch { /* noop */ }
        earlySuccessTimer = setTimeout(() => {
          try { updateFlowProgress('wealthica-flow', 100, 'Connected', 'All set'); } catch { /* noop */ }
          try { updateFlowToast('wealthica-flow', 'success', 'Connected'); } catch { /* noop */ }
        }, 900);
      }
    };

    const onPersistDeleted = () => {
      if (isFlowToastActive()) {
        try { dismissFlowToast('wealthica-flow'); } catch { /* noop */ }
      }
    };

    window.addEventListener('wealthica:persist:completed' as any, onPersistComplete);
    window.addEventListener('wealthica:positions:loaded' as any, onPositionsLoaded);
    window.addEventListener('wealthica:persist:deleted' as any, onPersistDeleted);
    return () => {
      window.removeEventListener('wealthica:persist:completed' as any, onPersistComplete);
      window.removeEventListener('wealthica:positions:loaded' as any, onPositionsLoaded);
      window.removeEventListener('wealthica:persist:deleted' as any, onPersistDeleted);
      try { if (earlySuccessTimer) clearTimeout(earlySuccessTimer); } catch { /* noop */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
